/* FD brand ---------------------------------------------------------------
   Measuring guide: "Dimensions, range by range" — tab behaviour and the
   per-range plate carousels. Ported from mocks/measuring-ranges.html,
   user-approved 2026-08-19.

   PROGRESSIVE ENHANCEMENT CONTRACT — read before editing.

   The section's DEFAULT markup is the finished no-JS state: every range panel
   is in the flow under its own visible heading, the tab bar carries the
   `hidden` attribute, and so do the carousel arrows. All of that is enforced by
   the user agent's own `[hidden]` rule, so the fallback survives this file
   failing AND fd-measure-ranges.css failing.

   A short inline bootstrap in the section flips the initial state before first
   paint (adds `.fd-mr--js`, reveals the tab bar, hides all but the first
   panel). This file, loaded with `defer`, only attaches behaviour on top of
   that. Both halves guard on the same rule: fewer than two range groups means
   no tab bar at all, and a group with one plate gets no arrows and no dots.

   No strings in this file are customer-facing: every label the reader sees
   comes from the section's own settings, and the control labels come from the
   markup the section rendered.
   ------------------------------------------------------------------------ */
(function () {
  'use strict';

  function initSection(root) {
    if (!root || root.fdMrReady) return;
    root.fdMrReady = true;

    /* ---------- carousels ---------- */
    var carousels = Array.prototype.slice.call(root.querySelectorAll('.fd-mr__carousel'));

    /* Every listener below hangs off an element inside the section and dies
       with it -- except the ones on `window`, which outlive the node the theme
       editor throws away. Collected here so the unload hook can release them. */
    root.fdMrSyncs = [];

    carousels.forEach(function (car) {
      var track = car.querySelector('.fd-mr__track');
      var prev = car.querySelector('.fd-mr__arrow--prev');
      var next = car.querySelector('.fd-mr__arrow--next');
      var dotWrap = car.querySelector('.fd-mr__dots');
      if (!track) return;

      var slides = Array.prototype.slice.call(track.querySelectorAll('.fd-mr__slide'));

      /* One plate (or none): no arrows, no dots, nothing to step through. */
      if (slides.length < 2) {
        car.setAttribute('data-single', '');
        return;
      }

      if (prev) prev.hidden = false;
      if (next) next.hidden = false;

      var dots = [];
      if (dotWrap) {
        slides.forEach(function (slide, i) {
          var dot = document.createElement('span');
          dot.className = 'fd-mr__dot';
          if (i === 0) dot.setAttribute('data-on', '');
          dotWrap.appendChild(dot);
          dots.push(dot);
        });
      }

      function step() {
        var gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        return slides[0].getBoundingClientRect().width + gap;
      }

      function sync() {
        var max = track.scrollWidth - track.clientWidth;
        if (prev) prev.disabled = track.scrollLeft <= 2;
        if (next) next.disabled = track.scrollLeft >= max - 2;
        var width = step();
        var i = width > 0 ? Math.round(track.scrollLeft / width) : 0;
        i = Math.max(0, Math.min(i, slides.length - 1));
        dots.forEach(function (dot, n) {
          if (n === i) {
            dot.setAttribute('data-on', '');
          } else {
            dot.removeAttribute('data-on');
          }
        });
      }
      car.fdSync = sync;

      if (prev) {
        prev.addEventListener('click', function () {
          track.scrollLeft -= step();
        });
      }
      if (next) {
        next.addEventListener('click', function () {
          track.scrollLeft += step();
        });
      }

      /* Called straight off the scroll event rather than through rAF: the work
         is two reads and an attribute flip, and rAF is throttled to nothing
         while the tab is in the background — which left the arrows stale on
         return. */
      track.addEventListener('scroll', sync, { passive: true });
      window.addEventListener('resize', sync);
      root.fdMrSyncs.push(sync);
      sync();
    });

    /* ---------- tabs ---------- */
    var tablist = root.querySelector('[role="tablist"]');
    if (!tablist) return;

    var tabs = Array.prototype.slice.call(tablist.querySelectorAll('[role="tab"]'));

    /* Nothing to switch between: leave the stacked view exactly as rendered. */
    if (tabs.length < 2) return;

    function panelFor(tab) {
      return document.getElementById(tab.getAttribute('aria-controls'));
    }

    function select(tab, moveFocus) {
      tabs.forEach(function (candidate) {
        var on = candidate === tab;
        var panel = panelFor(candidate);
        candidate.setAttribute('aria-selected', on ? 'true' : 'false');
        candidate.tabIndex = on ? 0 : -1;
        if (panel) panel.hidden = !on;
      });
      if (moveFocus) tab.focus();
      /* Keep the chosen chip in view when the bar is scrolled on a phone. */
      if (tab.scrollIntoView) {
        tab.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }
      /* Arrow and dot state can only be measured once the panel has a size. */
      var panel = panelFor(tab);
      var car = panel && panel.querySelector('.fd-mr__carousel');
      if (car && car.fdSync) car.fdSync();
    }

    tabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        select(tab, false);
      });
      tab.addEventListener('keydown', function (event) {
        var i = tabs.indexOf(tab);
        var target = null;
        if (event.key === 'ArrowRight') target = tabs[(i + 1) % tabs.length];
        else if (event.key === 'ArrowLeft') target = tabs[(i - 1 + tabs.length) % tabs.length];
        else if (event.key === 'Home') target = tabs[0];
        else if (event.key === 'End') target = tabs[tabs.length - 1];
        if (target) {
          event.preventDefault();
          select(target, true);
        }
      });
    });
  }

  function initAll() {
    Array.prototype.forEach.call(document.querySelectorAll('.fd-mr'), initSection);
  }

  initAll();

  /* The theme editor re-renders a section into a fresh DOM node. */
  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target.querySelector('.fd-mr');
    if (root) initSection(root);
  });

  /* ...and throws the old node away without unbinding its window listeners. */
  document.addEventListener('shopify:section:unload', function (event) {
    var root = event.target.querySelector && event.target.querySelector('.fd-mr');
    if (!root || !root.fdMrSyncs) return;
    root.fdMrSyncs.forEach(function (fn) {
      window.removeEventListener('resize', fn);
    });
    root.fdMrSyncs = [];
  });
})();
